<template>
  <gradient-wrapper v-if="renderPage">
    <form-card
      :show-action-section="false"
      :loading="loadingLoginPage"
    >
      <headline
        :text="headerText"
        data-cy="title"
      />
      <div
        v-if="!hideLoginForm && canRegister && !fromRedirect && isCompanyActive"
        id="ask-for-email"
        data-cy="register-option"
        class="mb-4"
      >
        <span class="sn-font-primary">{{ signInFirstTimeHere }}&nbsp;</span>
        <sn-btn
          :text="signInRegister"
          type="text"
          data-cy="register-btn"
          tabindex="6"
          @click="registerClicked"
        />
      </div>
      <v-form
        v-if="!hideLoginForm"
        ref="form"
        @submit.prevent
      >
        <sn-label
          class="mb-2"
          :title="signInEmailText"
          data-cy="email-label"
          label-for="user-email"
        />
        <sn-text-field
          id="email"
          v-model="userEmail"
          class="mb-4"
          data-cy="email-field"
          tabindex="1"
          :rules="rules.email"
          :aria-label="signInEmailText"
          validate-on-blur
          :autofocus="true"
          @focus="clearError"
          @keyup.native.enter="signInClicked"
        />
        <sn-label
          class="mb-2"
          :title="signInPasswordText"
          data-cy="password-label"
          label-for="user-password"
        />
        <sn-password
          v-model="userPassword"
          class="mb-4"
          sn-input-props_validate-on-blur
          tabindex="2"
          :sn-input-props_label="signInPasswordText"
          :sn-input-props_rules="rules.password"
          :sn-input-props_error-messages="errorMessage"
          @focus="clearError"
          @keydown.shift.tab.prevent="handleShiftTab"
          @keyup.native.enter="signInClicked"
        />
      </v-form>
      <v-row no-gutters>
        <sn-btn
          v-if="!hideLoginForm"
          size="full-width"
          data-cy="sign-in-btn"
          tabindex="3"
          :text="signInSignInButton"
          :loading="btnLoading"
          @click="signInClicked"
        />
      </v-row>
      <v-row
        v-if="!hideLoginForm && !showSsoBtn && showSsoBtnLO"
        class="mt-9"
        no-gutters
        :justify="$vuetify.breakpoint.mdAndDown ? 'center' : 'start'"
      >
        <sn-btn
          :text="useSsoLOText === 'USE SSO' ? 'Sign in with SSO (single sign-on)' : useSsoLOText"
          type="text"
          text-case="no-format"
          @click="redirectToLOSso()"
        />
      </v-row>
      <v-row
        v-if="showSsoBtn"
        :class="`${(hideLoginForm) ? 'mb-10': 'mt-9' }`"
        no-gutters
        :justify="$vuetify.breakpoint.mdAndDown ? 'center' : 'start'"
      >
        <sn-btn
          size="full-width"
          type="text"
          :text="useSsoText"
          @click="redirectToBorrowerSso()"
        />
      </v-row>
      <v-row
        v-if="!hideLoginForm"
        :class="`${(showSsoBtn || showSsoBtnLO) ? 'mb-5' : 'mb-10'} mt-9`"
        no-gutters
        :justify="$vuetify.breakpoint.mdAndDown ? 'center' : 'start'"
      >
        <sn-btn
          type="text"
          data-cy="forgot-password-link"
          tabindex="4"
          :text="signInForgotYourPassword"
          @click="recoverPasswordClicked"
        />
      </v-row>
    </form-card>
  </gradient-wrapper>
</template>

<script>
import FormCard from './Shared/FormCard.vue'
import GradientWrapper from './Shared/GradientWrapper.vue'
import cookie from 'js-cookie'
import snValidationsService from '~/services/snValidationsService.js'
import fetchServicerMixin from './Shared/fetchServicerMixin'
import brandThemeMixin from '~/components/common/brandThemeMixin'
import Headline from './Shared/Headline.vue'
import homehubMixin from './Shared/homehubMixin.js'
import {
  initialize,
  requestSignIn,
  signIn,
  requestHelp,
  start,
  verifyToken
} from './Shared/requests.js'

export default {
  name: 'SignIn',
  components: { GradientWrapper, FormCard, Headline },
  mixins: [homehubMixin, brandThemeMixin, fetchServicerMixin],
  data () {
    return {
      renderPage: false,
      userEmail: this.$store.userEmail || this.$route.query.email,
      userPassword: undefined,
      errorMessage: undefined,
      canTransitionHere: true,
      fromRedirect: !!this.$store.response?.data?.current_state_definition?.redirect && !!this.$store.response?.data?.current_state_definition?.new_lo,
      rules: {
        email: [
          (v) => !!v || this.signInEmailErrorMessage,
          (v) => this.emailIsValid || this.signInInvalidEmailErrorMessage
        ],
        password: [
          (v) => !!v || this.signInPasswordErrorMessage
        ]
      }
    }
  },
  computed: {
    loadingLoginPage () {
      const q = this.$apollo?.queries || {}
      return !!(q.noSacData?.loading || q.publicData?.loading)
    },
    isCompanyActive () {
      return this.$store.response?.data?.company_active
    },
    emailIsValid () {
      return !!this.userEmail && snValidationsService.email(this.userEmail)
    },
    authGuid () {
      return this.$store.authGuid || cookie.get('auth_guid')
    },
    headerText () {
      return this.fromRedirect ? this.$store.borrowerWebContent?.signInRedirectText : this.$store.borrowerWebContent?.welcome
    },
    signInFirstTimeHere () {
      return this.$store.borrowerWebContent?.signInFirstTimeHere
    },
    signInRegister () {
      return this.$store.borrowerWebContent?.signInRegister
    },
    signInEmailText () {
      return this.$store.borrowerWebContent?.signInEmailText
    },
    signInPasswordText () {
      return this.$store.borrowerWebContent?.signInPasswordText
    },
    signInForgotYourPassword () {
      return this.$store.borrowerWebContent?.signInForgotYourPassword
    },
    signInSignInButton () {
      return this.$store.borrowerWebContent?.signInSignInButton
    },
    signInEmailErrorMessage () {
      return this.$store.borrowerWebContent?.signInEmailErrorMessage || 'We need your email in order to help you sign in.'
    },
    signInPasswordErrorMessage () {
      return this.$store.borrowerWebContent?.signInPasswordErrorMessage || 'We need your password in order to help you sign in.'
    },
    signInInvalidEmailErrorMessage () {
      return this.$store.borrowerWebContent?.signInInvalidEmailErrorMessage || 'This doesn\'t appear to be a valid email.'
    },
    incorrectUsernameOrPass () {
      return this.$store.borrowerWebContent?.incorrectUsernameOrPass
    },
    resetYourPasswordErrorMessage () {
      return this.$store.borrowerWebContent?.resetYourPasswordErrorMessage
    },
    showSsoBtn () {
      return this.$store?.orgLoginSettings?.sso_setting_borrower_sso_enabled
    },
    showSsoBtnLO () {
      if (this.$route.params.code) {
        return false
      } else {
        return this.$store?.orgLoginSettings?.sso_setting_loan_officer_sso_v2
      }
    },
    hideLoginForm () {
      return this.$store.orgLoginSettings?.sso_setting_borrower_sso_hide_username_and_password_login
    },
    useSsoLOText () {
      return this.$store.orgLoginSettings?.sso_setting_loan_officer_sso_label
    },
    useSsoText () {
      return this.$store.orgLoginSettings?.sso_setting_borrower_sso_label
    },
    canRegister () {
      const hasCode = !!this.$route.params.code
      const noCodeWithCustomDomain = !!this.$store.defaultCode
      const allowLORegistration = !!this.$store.servicer?.is_discoverable || (!this.$store.servicer?.is_discoverable && !this.$store.servicer?.feature_setting_login_only_when_undiscoverable)
      return noCodeWithCustomDomain || (hasCode && allowLORegistration)
    },
    recoverPasswordExpiredOrInvalidTokenMessage () {
      return this.$store.borrowerWebContent?.recoverPasswordExpiredOrInvalidTokenMessage
    },
    recoverPasswordExpiredOrInvalidTokenDescription () {
      return this.$store.borrowerWebContent?.recoverPasswordExpiredOrInvalidTokenDescription
    }
  },
  watch: {
    // race condition occurs with page transition and apollo when token is present
    loadingFetchServicerData (newVal, oldVal) {
      if (!!oldVal && !newVal && !!this.$route.query.email_token) this.initiateTokenAuth()
    }
  },
  beforeCreate () {
    // used by brandThemeMixin to fetch custom themes
    this.$store.activationCode = this.$route.params.code
  },
  mounted () {
    if (!this.$route.query.email_token) this.initiateSignInAuth()
  },
  methods: {
    async initiateTokenAuth () {
      const emailToken = this.$route.query.email_token
      this.$router.replace({ path: this.$route.fullPath, query: { ...this.$route.query, email_token: undefined } }) // remove token from url

      this.$store.response = await verifyToken(emailToken)

      if (this.serverError) {
        this.setToast({
          event: 'error',
          message: this.recoverPasswordExpiredOrInvalidTokenMessage,
          description: this.recoverPasswordExpiredOrInvalidTokenDescription,
          type: 'sticky'
        })
        this.initiateSignInAuth()
      } else {
        this.$store.authGuid = this.$store.response.data.values.auth_guid
        cookie.set('auth_guid', this.$store.response.data.values.auth_guid)
        this.persistState()
        this.performTransition()
      }
    },
    handleShiftTab () {
      document.querySelector('input').focus()
    },
    initiateSignInAuth () {
      this.renderPage = true

      const calls = [
        {
          callback: initialize,
          onSuccess: () => {
            this.$store.authGuid = this.$store.response.data.values.auth_guid
            cookie.set('auth_guid', this.$store.response.data.values.auth_guid)
          }
        },
        {
          callback: () => requestSignIn({
            authGuid: this.authGuid,
            activationCode: this.$route.params.code
          })
        }
      ]

      const handleSuccess = () => {
        this.$store.userEmail = this.userEmail
      }

      this.chainApiCalls(calls, handleSuccess)
    },
    clearError () {
      this.errorMessage = undefined
    },
    async registerClicked () {
      this.$store.userClickedForgotPassword = false

      const calls = [
        {
          callback: initialize,
          onSuccess: () => {
            this.$store.authGuid = this.$store.response.data.values.auth_guid
            cookie.set('auth_guid', this.$store.response.data.values.auth_guid)
          }
        },
        { callback: () => start(this.$store.authGuid) }
      ]

      const handleSuccess = () => {
        this.persistState()
        this.performTransition()
      }

      this.chainApiCalls(calls, handleSuccess)
    },
    async recoverPasswordClicked () {
      this.$store.userClickedForgotPassword = true
      this.$store.response = await requestHelp(this.$store.authGuid)
      if (this.serverError) return this.handleApiError()

      this.persistState()
      this.performTransition()
    },
    async signInClicked () {
      if (!this.$refs.form.validate() || this.btnLoading) return
      this.$store.userEmail = this.userEmail
      this.btnLoading = true
      this.$store.userClickedForgotPassword = false

      this.$store.response = await signIn({
        authGuid: this.authGuid,
        authenticityToken: document.getElementsByName('csrf-token')[0].content,
        activationCode: this.$route.params.code,
        userEmail: this.userEmail,
        userPassword: this.userPassword
      })
      if (this.serverError) {
        if (this.$store.response?.data?.error) {
          this.btnLoading = false
          this.errorMessage = this.$store.response?.data?.error
        } else {
          return this.handleApiError()
        }
      } else {
        this.persistState()
        this.performTransition()
      }
    },
    redirectToBorrowerSso () {
      let url = '/homehub/sso_login'
      if (this.$store.activationCode) url += `/${this.$store.activationCode}`
      window.location.href = url
    },
    redirectToLOSso () {
      const url = '/homehub_sso'
      window.location.href = url
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

</style>
